/**
 * SoftDeleteConfirmModal (UI-SPEC W16) — destructive confirmation for asset
 * soft-delete. Direct analog of Phase 2's RevokeConfirmModal.
 *
 * Confirm fires DELETE /api/admin/assets/{id} via TanStack mutation; on
 * success, parent panel closes and the assets/geojson query is invalidated
 * so the asset disappears from the map.
 */
import { type SyntheticEvent, useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { useSoftDeleteAsset } from "../../api/assets";
import { Button } from "../../components/Button";
import { Banner } from "../../components/Banner";

interface SoftDeleteConfirmModalProps {
  assetId: number;
  assetNumber: string;
  onClose: () => void;
  onDeleted: () => void;
}

export function SoftDeleteConfirmModal({
  assetId,
  assetNumber,
  onClose,
  onDeleted,
}: SoftDeleteConfirmModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const softDelete = useSoftDeleteAsset();

  useEffect(() => {
    dialogRef.current?.showModal();
    return () => {
      dialogRef.current?.close();
    };
  }, []);

  const handleDialogCancel = (e: SyntheticEvent) => {
    // Use Cancel button only; native Esc is handled by parent panel
    // re-routing through onClose so the parent stays in sync.
    e.preventDefault();
    onClose();
  };

  const handleConfirm = () => {
    softDelete.mutate(assetId, { onSuccess: () => onDeleted() });
  };

  return (
    <dialog
      ref={dialogRef}
      onCancel={handleDialogCancel}
      data-testid="soft-delete-confirm-modal"
      className="rounded-lg border border-divider bg-surface-raised shadow-lg p-0 backdrop:bg-black/40 inline-size-[480px] max-inline-size-[calc(100vw-2rem)]"
    >
      <div className="ps-6 pe-6 pbs-6 pbe-6">
        <h2 className="text-2xl font-semibold text-on-surface mbe-4">
          <FormattedMessage
            id="LABEL_SOFT_DELETE_CONFIRM_HEADING"
            values={{ number: assetNumber }}
          />
        </h2>
        <p className="text-base text-text-secondary mbe-6">
          <FormattedMessage id="LABEL_SOFT_DELETE_CONFIRM_BODY" />
        </p>

        {softDelete.isError && (
          <div className="mbe-4">
            <Banner variant="error" data-testid="soft-delete-error-banner">
              <FormattedMessage id={softDelete.error?.code ?? "ERR_GENERIC"} />
            </Banner>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={softDelete.isPending}
            data-testid="soft-delete-cancel-button"
          >
            <FormattedMessage id="LABEL_CANCEL" />
          </Button>
          <Button
            variant="destructive"
            type="button"
            onClick={handleConfirm}
            disabled={softDelete.isPending}
            data-testid="soft-delete-confirm-button"
          >
            <FormattedMessage id="LABEL_SOFT_DELETE" />
          </Button>
        </div>
      </div>
    </dialog>
  );
}
